import { computed, ref, Ref, watch, MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';
import { anykrowdApi } from '@/api/anykrowdApi';
import { useQuery, useQueries } from '@tanstack/vue-query';

import { Post } from '@/shared/interfaces/post';
import useStorageService from '@/shared/composables/useStorageService';
import { ValuesEnum } from '@/enums/values.enum';
import { useCacheImage } from '@/shared/composables/useCacheImage';
// Init
const { get, set } = useStorageService();

// Get post detail
const getPostDetail = async (postId: number): Promise<Post> => {
	const { loadImage } = useCacheImage();

	const { data } = await anykrowdApi.get<Post>(`/post/detail/${postId}`, false);

	// Cache images
	if (data.images) {
		data.images.forEach(async (image, index) => {
			let postImg = image.image_url.split('/').pop();
			postImg = postImg ?? 'post';
			await loadImage(`post-${postId}-image-${index}`, postImg, image.image_url);
		});
	}

	return data;
};

// getPost
const getPost = (postId: MaybeRefOrGetter<number>, enabled: MaybeRefOrGetter<boolean> = true) => {
	// Init
	const post = ref<Post>()!;

	const {
		data: postData,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: computed(() => ['post', toValue(postId)]),
		queryFn: () => getPostDetail(toValue(postId)),
		cacheTime: 1000 * 60 * 60 * 24 * 365,
		retry: 3,
		enabled: computed(() => toValue(enabled)),
	});

	watch(
		postData,
		() => {
			if (postData.value) {
				post.value = { ...postData.value };

				// Update cache
				const currentPostId = toValue(postId);
				set(`post-${currentPostId}`, postData.value);
			}
		},
		{ immediate: true },
	);

	return {
		isFetchedPost: computed(() => isFetched.value),
		isFetchingPost: computed(() => isFetching.value),
		post,
	};
};

// getPosts
const getPosts = (postsIds: Ref<number[]>) => {
	// Get post
	const posts = useQueries({
		queries: postsIds.value.length
			? postsIds.value.map((id) => {
					return {
						queryKey: ['post', id],
						queryFn: () => getPost(id),
					};
				})
			: [],
	});

	return {
		posts,
	};
};

// usePosts
const usePosts = () => {
	const currentMainAlertPost = ref<Post | null>();

	// setCurrentMainAlert
	const setCurrentMainAlert = async (post: Post): Promise<Post | null> => {
		if (!post) {
			return null;
		}

		const mainAlertIDsPrompted = (await get<number[]>(ValuesEnum.StoreListOfMainAlertIDsPrompted)) || [];
		// if we already prompted this main alert we will ignore the main alert
		if (mainAlertIDsPrompted.includes(post.id)) {
			currentMainAlertPost.value = null;

			return null;
		}
		currentMainAlertPost.value = post;
		return post;
	};

	// markMainAlertAsSeenInCache
	const markMainAlertAsSeenInCache = async (post: Post) => {
		const currentListOfMainAlerts = (await get<number[]>(ValuesEnum.StoreListOfMainAlertIDsPrompted)) || [];

		currentMainAlertPost.value = null;

		if (currentListOfMainAlerts.includes(post.id)) {
			return;
		}
		await set(ValuesEnum.StoreListOfMainAlertIDsPrompted, [...currentListOfMainAlerts, post.id]);
	};

	// markPostAsViewed
	const markPostAsViewed = (postId: number) => {
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		anykrowdApi.post(`/post/${postId}/view`, {}, true).catch(() => {});
	};

	// postSetMainAlertAsSeen
	const postSetMainAlertAsSeen = async (post: Post): Promise<any> => {
		markMainAlertAsSeenInCache(post);
		const { data } = await anykrowdApi.post(`${window.apiUrl}/post/main-alert/mark-as-seen/${post.id}`, {}, true);
		return data;
	};

	return {
		currentMainAlertPost,
		getPost,
		getPosts,
		getPostDetail,
		markMainAlertAsSeenInCache,
		markPostAsViewed,
		postSetMainAlertAsSeen,
		setCurrentMainAlert,
	};
};

export default usePosts;
